<template>
    <SimplePlane
        ref="refBg"
        :material="material"
        :layer="LAYER_BG"
        :bounds="bounds"
        :visible="active"
    />
</template>

<script setup>
    import { AdditiveBlending, Color, RepeatWrapping } from 'three';
    import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';

    import { lerp } from '@resn/gozer-math';
    import { CustomMaterial } from '@resn/gozer-three/materials';
    import { usePane, useRafBool, useResize } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import glslFill from '@/libs/lygia/draw/fill.glsl';
    import glslConst from '@/libs/lygia/math/const.glsl';
    import glslCircleSdf from '@/libs/lygia/sdf/circleSDF.glsl';
    import glslRotate from '@/libs/lygia/space/rotate.glsl';
    import SimplePlane from '~/components/gl/common/SimplePlane.vue';
    import { glslSpeedLines } from '~/components/gl/shaders/ShaderChunks/speed-lines';
    import { LAYER_BG } from '~/core/constants';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const refBg = ref(null);

    const active = ref(false);
    const progress = ref(0);
    const uSwirlProgress = ref(0);
    const uSpinSpeed = ref(1.2);
    const uToonThreshold = ref(0.3);
    const uRadiusScale = ref(0.08);
    const uAngleScale = ref(10.0);

    const viewport = useResize();
    const bounds = computed(() => {
        const max = Math.max(viewport.width, viewport.height);
        return { width: max, height: max };
    });

    const color = ref('#9486ff');

    onMounted(() => {
        refBg.value.mesh.position.z = 0.1;
    });

    const material = new CustomMaterial({
        uniforms: {
            uTime: 0,
            uProgress: 0,
            uSwirlProgress: 0,
            uSpinSpeed: 0,
            uNoiseMap: null,
            uToonThreshold: 0,
            uRadiusScale: 0,
            uAngleScale: 0,
            uColor: new Color(),
        },
        fs: `
            uniform float uTime;
            uniform float uProgress;
            uniform float uSwirlProgress;
            uniform float uSpinSpeed;
            uniform sampler2D uNoiseMap;
            uniform float uToonThreshold;
            uniform float uRadiusScale;
            uniform float uAngleScale;
            uniform vec3 uColor;
            varying vec2 vUv;

            ${glslConst}
            ${glslCircleSdf}
            ${glslRotate}
            ${glslFill}
            ${glslSpeedLines}

            void main() {
                float radius = length(vUv - 0.5);
        
                float spinSpeed = -uTime * uSpinSpeed;
                vec2 noiseUv = vUv;
                float speedLinesNoise1 = speedLines(
                    uNoiseMap,
                    noiseUv,
                    spinSpeed,  
                    uRadiusScale,
                    uAngleScale
                );
                float speedLinesNoise2 = speedLines(
                    uNoiseMap,
                    noiseUv,
                    spinSpeed * 0.25,  
                    uRadiusScale,
                    uAngleScale * 0.25
                );
                
                float sdf = mix(speedLinesNoise1, speedLinesNoise2, 0.5);
                // sdf *= mix(0.0, 0.99, smoothstep(0.6, 0.0, radius));
                sdf *= smoothstep(0.0, 0.15, radius);
                
                float mask = 1.0 - fill(sdf, mix(0.5, uToonThreshold, uProgress));
                
                float alpha = mask;

                if (alpha == 0.0) discard;

                gl_FragColor = vec4(uColor, alpha);
                // gl_FragColor = vec4(vec3(mask), 1.0);
                
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        options: {
            name: 'BackgroundGradient',
            depthWrite: false,
            depthTest: false,
            blending: AdditiveBlending,
        },
    });

    useGlobalAssets(({ data }) => {
        const { noise1 } = data;
        noise1.wrapS = noise1.wrapT = RepeatWrapping;
        noise1.needsUpdate = true;

        material.uniforms.uNoiseMap.value = noise1;
    });

    let tl = null;
    const show = ({ delay = 0 } = {}) => {
        tl?.kill();

        const duration = 0.5;

        tl = gsap.timeline({
            delay,
            onStart: () => (active.value = true),
        });
        tl.fromTo(progress, { value: 0 }, { value: 1, duration });
    };

    const hide = ({ delay = 0, swirl = true } = {}) => {
        tl?.kill();

        const duration = 0.5;

        tl = gsap.timeline({
            delay,
            onStart: () => (active.value = true),
            onComplete: () => (active.value = false),
        });
        tl.to(progress, { value: 0, duration });
    };

    watch(progress, (val) => {
        material.uniforms.uProgress.value = val;
    });

    watchEffect(() => {
        material.uniforms.uSpinSpeed.value = uSpinSpeed.value;
        material.uniforms.uToonThreshold.value = uToonThreshold.value;
        material.uniforms.uRadiusScale.value = uRadiusScale.value;
        material.uniforms.uAngleScale.value = uAngleScale.value;
        material.uniforms.uColor.value.set(color.value);
    });

    useRafBool(active, ({ timestamp }) => {
        material.uniforms.uTime.value = timestamp * 0.001;
    });

    const destroy = () => {
        material.dispose();
    };

    onBeforeUnmount(destroy);

    defineExpose({ show, hide });

    const paneOptions = {
        min: 0,
        max: 1,
        step: 0.01,
    };

    usePane(
        [
            { name: 'progress', value: progress, options: paneOptions },
            { name: 'uSwirlProgress', value: uSwirlProgress, options: paneOptions },
            { name: 'uSpinSpeed', value: uSpinSpeed, options: { min: 0, max: 3, step: 0.01 } },
            { name: 'uToonThreshold', value: uToonThreshold, options: paneOptions },
            { name: 'uRadiusScale', value: uRadiusScale, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'uAngleScale', value: uAngleScale, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'active', value: active },
            { name: 'color', value: color },
        ],
        { title: 'Speed Lines' }
    );
</script>
